import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, FileText } from 'lucide-react';
import TaskStatusBadge from './TaskStatusBadge';
import TaskPriorityBadge from './TaskPriorityBadge';
import TaskActions from './TaskActions';
import VerificationMethodBadge from './VerificationMethodBadge';
import { EVIDENCE_TIER_MAP } from '../../../constants/evidenceTiers';

const TaskRow = ({ task, onTaskUpdate, onTaskClick }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
      {/* Main Row */}
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Expand Button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 mt-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {expanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                    {task.control_id}
                  </span>
                  <TaskPriorityBadge priority={task.priority} />
                  <VerificationMethodBadge
                    verificationMethod={task.verification_method}
                    integrationSource={task.integration_source}
                  />
                </div>
                <h4
                  className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-teal-600 transition-colors"
                  onClick={() => onTaskClick && onTaskClick(task)}
                >
                  {task.title || task.description}
                </h4>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <TaskStatusBadge
                  status={task.status}
                  integrationSource={task.integration_source}
                />
                {task.evidence_tier && EVIDENCE_TIER_MAP[task.evidence_tier] && (
                  <span
                    className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${EVIDENCE_TIER_MAP[task.evidence_tier].color}`}
                    title={EVIDENCE_TIER_MAP[task.evidence_tier].label}
                  />
                )}
              </div>
            </div>

            {/* Category */}
            {task.category && (
              <div className="mb-2">
                <span className="text-xs text-gray-500">
                  Category: <span className="font-medium text-gray-700">{task.category}</span>
                </span>
              </div>
            )}

            {/* Actions */}
            <TaskActions task={task} onTaskUpdate={onTaskUpdate} onTaskClick={onTaskClick} />
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 ml-9">
            <div className="space-y-3">
              {/* Description */}
              {task.description && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Description</h5>
                  <p className="text-sm text-gray-600">{task.description}</p>
                </div>
              )}

              {/* Evidence URL */}
              {task.evidence_url && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Evidence</h5>
                  <a
                    href={task.evidence_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Evidence
                  </a>
                </div>
              )}

              {/* Notes */}
              {task.notes && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Notes</h5>
                  <p className="text-sm text-gray-600">{task.notes}</p>
                </div>
              )}

              {/* Integration Info */}
              {task.integration_source && task.status === 'auto_verified' && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-700 mb-1">Verification Details</h5>
                  <div className="flex items-start gap-2 text-xs text-gray-600">
                    <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span>
                      Automatically verified via {task.integration_source} integration
                      {task.verified_at && ` on ${new Date(task.verified_at).toLocaleDateString()}`}
                    </span>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="flex items-center gap-4 text-xs text-gray-500">
                {task.created_at && (
                  <span>Created: {new Date(task.created_at).toLocaleDateString()}</span>
                )}
                {task.updated_at && task.updated_at !== task.created_at && (
                  <span>Updated: {new Date(task.updated_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskRow;
